// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnChange,
  useOnMount,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { AsyncUtils } from '../../../../common/utils';
import { AppRoute } from '../../../app';
import { EmptyState, LoadingWrapper, Panel, TabPanel } from '../../../../common/components';
import { selectPendingActions } from '../../../../common/state/pending';
import {
  selectCampaignDetails,
  selectCampaignProducts,
  selectCampaignSeries,
  selectCampaignSeriesSegments,
} from '../../state/campaignSelectors';
import {
  createCampaignSeries,
  closeCampaign,
  fetchCampaign,
  fetchCampaignProducts,
  fetchCampaignSeries,
  fetchSeriesSegments,
  updateCampaign,
  updateCampaignSeries,
} from '../../state/campaignActions';
import CampaignService from '../../services/CampaignService';
import CampaignDetailsTab from './partials/CampaignDetailsTab';
import CampaignSeriesTab from './partials/CampaignSeriesTab';

const TabIndex = {
  Details: 0,
  Series: 1,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  fullHeight: {
    flex: '1',
    height: '100%',
  },
}));

function CampaignDetailsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Details);
  const [showActiveSeries, setShowActiveSeries] = useHistoryState('activeSeries', true);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const { campaignId } = useParams();
  const history = useHistory();
  const campaignDetails = useSelector(selectCampaignDetails);
  const campaignSeries = useSelector(selectCampaignSeries);
  const campaignProducts = useSelector(selectCampaignProducts);
  const pendingActions = useSelector(selectPendingActions);
  const campaignSeriesSegments = useSelector(selectCampaignSeriesSegments);
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!campaignDetails) {
          setLoading(true);
        }
        await AsyncUtils.executeAll([
          dispatch(fetchCampaign(campaignId)),
          dispatch(fetchCampaignSeries(campaignId, showActiveSeries)),
          dispatch(fetchCampaignProducts()),
          dispatch(fetchSeriesSegments()),
        ]);
      } catch (e) {
        showAlert({
          title: t('campaign.campaignDetailsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }
    handleFetchData();
  });

  const handleUpdateCampaign = useCallback(
    async campaignData => {
      try {
        await dispatch(updateCampaign(campaignId, campaignData));
        showAlert({
          title: t('common.success'),
          message: t('campaign.campaignUpdateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('campaign.campaignUpdateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, t]
  );

  const handleFetchCampaignSeries = useCallback(async () => {
    try {
      await dispatch(fetchCampaignSeries(campaignId, showActiveSeries));
    } catch (e) {
      showAlert({
        title: t('campaign.campaignSeriesFetchError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [campaignId, dispatch, showActiveSeries, showAlert, t]);

  const handleCreateCampaignSeries = useCallback(
    async seriesData => {
      try {
        await dispatch(createCampaignSeries(seriesData));
        await dispatch(fetchCampaignSeries(campaignId, showActiveSeries));
        showAlert({
          title: t('common.success'),
          message: t('campaign.campaignSeriesCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('campaign.campaignSeriesCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [campaignId, dispatch, showActiveSeries, showAlert, t]
  );

  const handleUpdateCampaignSeries = useCallback(
    async seriesData => {
      try {
        await dispatch(updateCampaignSeries(seriesData));
        await dispatch(fetchCampaignSeries(campaignId, showActiveSeries));
        showAlert({
          title: t('common.success'),
          message: t('campaign.campaignSeriesUpdateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('campaign.campaignSeriesUpdateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [campaignId, dispatch, showActiveSeries, showAlert, t]
  );

  const handleDownloadVouchers = campaignSeriesId => {
    window.location.href = CampaignService.getDownloadVouchersUrl(campaignSeriesId);
    _.delay(() => {
      showAlert({
        title: t('common.success'),
        message: t('campaign.campaignSeriesDownloadVouchersSuccess'),
        severity: AlertSeverity.Success,
      });
      handleFetchCampaignSeries();
    }, 500);
  };

  const handleGoBack = useCallback(() => {
    history.push(_.defaultTo(history.location.state?.from, AppRoute.Campaigns));
  }, [history]);

  useOnChange(showActiveSeries, () => {
    handleFetchCampaignSeries();
  });

  const handleCloseCampaign = useCallback(async () => {
    try {
      await dispatch(closeCampaign(campaignId));
      await dispatch(fetchCampaign(campaignId));
      showAlert({
        title: t('common.success'),
        message: t('campaign.campaignCloseSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('campaign.campaignCloseError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, campaignId]);

  return (
    <Box id="CampaignDetails-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel
          id="CampaignDetails-panel"
          title={t('campaign.campaignDetails')}
          className={clsx(classes.panel, {
            [classes.fullHeight]: tabIndex !== TabIndex.Details,
          })}
        >
          {!campaignDetails ? (
            <EmptyState variant="unavailable" message={t('campaign.campaignDetailsNotAvailable')} />
          ) : (
            <>
              <Tabs
                id="CampaignDetails-tabs"
                className={classes.tabs}
                data-value={tabIndex}
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, index) => setTabIndex(index)}
              >
                <Tab
                  id="CampaignDetails-detailsTab"
                  label={t('campaign.campaign')}
                  data-index={TabIndex.Details}
                />
                <Tab
                  id="CampaignDetails-seriesTab"
                  label={t('campaign.campaignSeries')}
                  data-index={TabIndex.Series}
                />
              </Tabs>
              <Box id="CampaignDetails-tabsContent" className={classes.fullHeight}>
                <TabPanel index={TabIndex.Details} currentIndex={tabIndex}>
                  <CampaignDetailsTab
                    campaign={campaignDetails}
                    series={_.defaultTo(campaignSeries, [])}
                    products={_.defaultTo(campaignProducts, [])}
                    onUpdateCampaign={handleUpdateCampaign}
                    onGoBack={handleGoBack}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                    onCloseCampaign={handleCloseCampaign}
                  />
                </TabPanel>
                <TabPanel index={TabIndex.Series} currentIndex={tabIndex}>
                  <CampaignSeriesTab
                    campaignId={campaignDetails.id}
                    series={_.defaultTo(campaignSeries, [])}
                    seriesSegments={_.defaultTo(campaignSeriesSegments, [])}
                    showActive={showActiveSeries}
                    onCreateSeries={handleCreateCampaignSeries}
                    onUpdateSeries={handleUpdateCampaignSeries}
                    onDownloadVouchers={handleDownloadVouchers}
                    onFilterChange={() => setShowActiveSeries(!showActiveSeries)}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
              </Box>
            </>
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default CampaignDetailsPage;